'use client';

import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  fetchTomVendingSiteVisitHistoryDetail,
  type TomVendingSiteVisitHistoryMachine,
  type TomVendingSiteVisitCashCollection,
} from '@/hooks/useTomVendingSiteVisitHistory';
import { formatUsdFromCents } from '@/hooks/useTomVendingReports';
import { useNotification } from '@/contexts/NotificationContext';
import TomsVendingSiteVisitDetailContent, {
  formatVisitDateLabel,
} from '../toms-vending/TomsVendingSiteVisitDetailContent';

const TomsVendingSiteVisitDetailPage: React.FC = () => {
  const { siteVisitId: siteVisitIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const siteVisitId = parseInt(String(siteVisitIdParam ?? ''), 10);
  const locationIdParam = parseInt(String(searchParams.get('locationId') ?? ''), 10);
  const locationTomVendingId = Number.isNaN(locationIdParam) ? undefined : locationIdParam;

  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [visitDate, setVisitDate] = useState('');
  const [locationName, setLocationName] = useState('');
  const [resolvedLocationId, setResolvedLocationId] = useState<number | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string | null>(null);
  const [machines, setMachines] = useState<TomVendingSiteVisitHistoryMachine[]>([]);
  const [cashCollections, setCashCollections] = useState<TomVendingSiteVisitCashCollection[]>([]);
  const [siteCashTotalCents, setSiteCashTotalCents] = useState(0);

  const load = useCallback(async () => {
    if (Number.isNaN(siteVisitId) || siteVisitId <= 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const d = await fetchTomVendingSiteVisitHistoryDetail({
        siteVisitId,
        locationTomVendingId,
      });
      setVisitDate(d.visitDate);
      setLocationName(d.locationName);
      setResolvedLocationId(d.locationTomVendingId);
      setUserDisplayName(d.userDisplayName);
      setMachines(d.machines);
      setCashCollections(d.cashCollections ?? []);
      setSiteCashTotalCents(d.siteCashTotalCents ?? 0);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load site visit');
      setMachines([]);
    } finally {
      setLoading(false);
    }
  }, [siteVisitId, locationTomVendingId, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  if (Number.isNaN(siteVisitId) || siteVisitId <= 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Invalid site visit id.</Typography>
      </Box>
    );
  }

  const locId = resolvedLocationId ?? locationTomVendingId;
  const locLabel = locationName.trim() || (locId != null ? `Location ${locId}` : 'Location');
  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Reports', href: '/app/toms-vending/reports' },
    ...(locId != null
      ? [{ label: locLabel, href: `/app/toms-vending/locations/${locId}?tab=history` }]
      : []),
    { label: visitDate ? formatVisitDateLabel(visitDate) : 'Site visit' },
  ];

  return (
    <Box>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title={visitDate ? formatVisitDateLabel(visitDate) : 'Site visit'}
      />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Visit #{siteVisitId}
          {userDisplayName ? ` · ${userDisplayName}` : ''}
          {siteCashTotalCents > 0 ? ` · Cash collected ${formatUsdFromCents(siteCashTotalCents)}` : ''}
          {locId != null ? (
            <>
              {' · '}
              <RouterLink to={`/app/toms-vending/locations/${locId}?tab=history`}>
                {locLabel}
              </RouterLink>
            </>
          ) : null}
        </Typography>
        {cashCollections.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Cash collected
            </Typography>
            {cashCollections.map((c, idx) => (
              <Typography key={idx} variant="body2" color="text.secondary">
                {c.vendingMachineId != null
                  ? c.assetId?.trim() || `Machine #${c.vendingMachineId}`
                  : 'Site total'}
                {' · '}
                Bills {formatUsdFromCents(c.billsCents)} · Coins {formatUsdFromCents(c.coinsCents)} ·{' '}
                <strong>{formatUsdFromCents(c.totalCents)}</strong>
                {c.notes ? ` · ${c.notes}` : ''}
              </Typography>
            ))}
          </Box>
        ) : null}
        <TomsVendingSiteVisitDetailContent loading={loading} machines={machines} />
      </Box>
    </Box>
  );
};

export default TomsVendingSiteVisitDetailPage;
